/** @jsx jsx */
import { css, jsx } from "@emotion/react"
import { useTheme } from "components/common/Context/ThemeContext"
import PropTypes from "prop-types"
import { useMemo } from "react"

export const fontSizes = Object.freeze({
  smaller: "0.75rem",
  small: "0.875rem",
  normal: "1rem",
  large: "1.125rem",
  larger: "1.25rem"
})

export const fontWeights = Object.freeze({
  light: "fontFamily",
  medium: "fontFamilyMedium",
  semiBold: "fontFamilySemiBold"
})

export const StandardTypographyTypes = Object.freeze({
  subHeading: "Sub-Heading",
  cardHeading: "Card-heading",
  cardSubHeading: "Card-SubHeading",
  cardContent: "Card-Content"
})

const CMSTypography = ({
  type,
  fontSize,
  fontWeight,
  color,
  disabledColor,
  children,
  clickable,
  onClick,
  disabled,
  href,
  dataTestId,
  dataTut,
  element,
  italic
}) => {
  const lineheight = useMemo(() => {
    if (fontSize === "0.75rem") return "1.25rem"
    else if (
      fontSize === "0.875rem" ||
      type === StandardTypographyTypes.cardSubHeading ||
      type === StandardTypographyTypes.cardContent
    )
      return "1.375rem"
    else if (
      fontSize === "1rem" ||
      type === StandardTypographyTypes.cardHeading
    )
      return "1.5rem"
    else if (
      fontSize === "1.125rem" ||
      type === StandardTypographyTypes.subHeading
    )
      return "1.625rem"
    else if (fontSize === "1.25rem") return "1.75rem"
  }, [fontSize, type])

  const useStyles = (theme) => {
    return {
      customText: css`
        ${type === StandardTypographyTypes.subHeading
          ? `
          font-size:${fontSizes.large};
          font-family:${theme[fontWeights.medium]};
          line-height: ${lineheight};
          `
          : ""}
        ${type === StandardTypographyTypes.cardHeading
          ? `
          font-size:${fontSizes.normal};
          font-family:${theme[fontWeights.medium]};
          line-height: ${lineheight};
          `
          : ""}
        ${type === StandardTypographyTypes.cardSubHeading
          ? `
          font-size:${fontSizes.small};
          font-family:${theme[fontWeights.medium]};
          line-height: ${lineheight};
          `
          : ""}
        ${type === StandardTypographyTypes.cardContent
          ? `
          font-size:${fontSizes.small};
          font-family:${theme[fontWeights.light]};
          line-height: ${lineheight};
          `
          : ""}
        ${!type
          ? `
          font-size:${fontSize || fontSizes.small};
          font-family: ${
            fontWeight ? theme[fontWeight] : theme[fontWeights.light]
          };
          line-height: ${lineheight};
          `
          : ""}
        ${disabled
          ? `
          color: ${
            disabledColor ||
            color ||
            (clickable && href
              ? theme.externalLinkColor
              : theme.typographyDisabled)
          };
        `
          : `
        color: ${
          color ||
          (clickable && href ? theme.externalLinkColor : theme.primaryTextColor)
        };
        `}
        cursor: ${clickable
          ? disabled
            ? "not-allowed"
            : "pointer"
          : "inherit"};
        ${italic ? "font-style: italic;" : ""}
        border: 0;
        padding: 0;
        background: none;
        font-weight: normal;
        &:hover {
          opactiy: 0.9 !important;
        }
        ${clickable && href && "text-decoration: underline;"}
      `,
      clickableText: css`
        text-align: left;
        &:hover {
          text-decoration: underline;
          ${clickable &&
          href &&
          `color:${color || theme.externalLinkColorHover} !important`}
        }
        ,
        &:active {
          text-decoration: underline;
        }
      `
    }
  }

  const theme = useTheme()
  const classes = useStyles(theme)
  if (clickable && !disabled) {
    if (href)
      return (
        <a
          onClick={onClick}
          href={href}
          target={"_blank"}
          css={[classes.customText, classes.clickableText]}
          data-testid={dataTestId}
          data-tut={dataTut}
          rel="noopener noreferrer"
        >
          {children}
        </a>
      )
    return (
      <button
        onClick={onClick}
        disabled={disabled}
        css={[classes.customText, classes.clickableText]}
        data-testid={dataTestId}
        data-tut={dataTut}
      >
        {children}
      </button>
    )
  }

  if (element === "span")
    return (
      <span
        data-testid={dataTestId}
        data-tut={dataTut}
        css={[classes.customText]}
      >
        {children}
      </span>
    )

  return (
    <p data-testid={dataTestId} data-tut={dataTut} css={[classes.customText]}>
      {children}
    </p>
  )
}

export default CMSTypography

CMSTypography.propTypes = {
  color: PropTypes.string,
  disabledColor: PropTypes.string,
  children: PropTypes.node,
  fontWeight: PropTypes.string,
  fontSize: PropTypes.string,
  type: PropTypes.string,
  disabled: PropTypes.bool,
  clickable: PropTypes.bool,
  onClick: PropTypes.func,
  href: PropTypes.string,
  dataTestId: PropTypes.string,
  dataTut: PropTypes.string,
  element: PropTypes.string,
  italic: PropTypes.bool
}
